import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import AppDatePicker, { INCOMPLETE_DATE_MESSAGE } from '@/components/common/AppDatePicker';
import { AdminService } from '@/services/admin.service';
import { Issuer, CreateControlTypeRequest } from '@/types';
import ControlPeriodSelect, { controlPeriodMonthsError, controlPeriodPayload } from '@/components/admin/ControlPeriodSelect';
import { DEFAULT_PERIOD_CONFIG, type ControlPeriodConfig } from '@shared/control-period';
import { CacheManager, CACHE_KEYS } from '@/utils/cache';
import { toDateOnlyString } from '@/utils/format';

interface CreateControlTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateControlTypeRequest) => Promise<void>;
  isLoading?: boolean;
}

const CreateControlTypeModal: React.FC<CreateControlTypeModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [issuers, setIssuers] = useState<Issuer[]>([]);
  const [isLoadingIssuers, setIsLoadingIssuers] = useState(false);
  const [formData, setFormData] = useState<CreateControlTypeRequest>({
    name: '',
    issuerId: '',
    version: '',
    description: '',
    formConfiguration: { fields: [] },
  });

  const [period, setPeriod] = useState<ControlPeriodConfig>(DEFAULT_PERIOD_CONFIG);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeFromDate, setActiveFromDate] = useState<Date | null>(new Date());
  const [activeUntilDate, setActiveUntilDate] = useState<Date | null>(null);
  // Half-typed dates are null to the form; they must not pass as empty.
  const [activeFromInvalid, setActiveFromInvalid] = useState(false);
  const [activeUntilInvalid, setActiveUntilInvalid] = useState(false);

  // Load issuers when modal opens
  useEffect(() => {
    if (open) {
      setIsLoadingIssuers(true);
      AdminService.getIssuers()
        .then((response) => {
          // API returns { issuers: Issuer[], total, page, limit, totalPages }
          const issuerList = response?.issuers;
          setIssuers(Array.isArray(issuerList) ? issuerList : []);
        })
        .catch(error => {
          console.error('Failed to load issuers:', error);
          setIssuers([]); // Set empty array on error
        })
        .finally(() => {
          setIsLoadingIssuers(false);
        });
    }
  }, [open]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors['name'] = 'Namn är obligatoriskt';
    }

    if (!formData.issuerId) {
      newErrors['issuerId'] = 'Utfärdare är obligatorisk';
    }

    if (!formData.version?.trim()) {
      newErrors['version'] = 'Version är obligatorisk';
    }

    if (!formData.description?.trim()) {
      newErrors['description'] = 'Beskrivning är obligatorisk';
    }

    if (activeFromInvalid) {
      newErrors['activeFrom'] = INCOMPLETE_DATE_MESSAGE;
    } else if (!activeFromDate) {
      newErrors['activeFrom'] = 'Aktiv från-datum är obligatoriskt';
    }

    if (activeUntilInvalid) {
      newErrors['activeUntil'] = INCOMPLETE_DATE_MESSAGE;
    } else if (activeUntilDate && activeFromDate && activeUntilDate <= activeFromDate) {
      newErrors['activeUntil'] = 'Aktiv till-datum måste vara efter aktiv från-datum';
    }

    const monthsError = controlPeriodMonthsError(period);
    if (monthsError) {
      newErrors['periodMonths'] = monthsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData: CreateControlTypeRequest = {
        name: formData.name,
        issuerId: formData.issuerId,
        description: formData.description,
        formConfiguration: formData.formConfiguration,
        activeFrom: toDateOnlyString(activeFromDate!),
        ...controlPeriodPayload(period),
        ...(formData.version && { version: formData.version }),
        ...(formData.inspectionFrequency && { inspectionFrequency: formData.inspectionFrequency }),
        ...(formData.notes && { notes: formData.notes }),
        ...(activeUntilDate && { activeUntil: toDateOnlyString(activeUntilDate) }),
      };

      await onSubmit(submitData);
      
      // Clear the control types cache so the CreateControlPage will refresh
      CacheManager.remove(CACHE_KEYS.PREDEFINED_CONTROL_TYPES);
      console.log('🧹 Cache cleared after creating control type');
      console.log('🧹 Cache should be invalidated for key:', CACHE_KEYS.PREDEFINED_CONTROL_TYPES);
      
      handleClose();
    } catch (error) {
      console.error('Error creating control type:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      issuerId: '',
      version: '',
      description: '',
      formConfiguration: { fields: [] },
    });
    setPeriod(DEFAULT_PERIOD_CONFIG);
    setErrors({});
    setActiveFromDate(new Date());
    setActiveUntilDate(null);
    onClose();
  };

  const handleChange = (field: keyof CreateControlTypeRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { minHeight: '600px' }
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Skapa ny kontrolltyp
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Namn"
                  value={formData.name}
                  onChange={handleChange('name')}
                  error={!!errors['name']}
                  helperText={errors['name']}
                  required
                  placeholder="t.ex. KRAV Ekologisk Grönsaksproduktion"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Utfärdare"
                  value={formData.issuerId}
                  onChange={handleChange('issuerId')}
                  error={!!errors['issuerId']}
                  helperText={errors['issuerId'] || (isLoadingIssuers ? 'Laddar utfärdare...' : '')}
                  required
                  disabled={isLoadingIssuers}
                >
                  {isLoadingIssuers ? (
                    <MenuItem disabled>Laddar...</MenuItem>
                  ) : (issuers || []).length === 0 ? (
                    <MenuItem disabled>Inga utfärdare tillgängliga</MenuItem>
                  ) : (
                    (issuers || []).map((issuer) => (
                      <MenuItem key={issuer.id} value={issuer.id}>
                        {issuer.displayName} ({issuer.code})
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Version"
                  value={formData.version}
                  onChange={handleChange('version')}
                  error={!!errors['version']}
                  helperText={errors['version']}
                  required
                  placeholder="t.ex. 2025.1"
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <AppDatePicker
                  label="Aktiv från"
                  value={activeFromDate}
                  onChange={(newValue) => {
                    setActiveFromDate(newValue);
                    if (errors['activeFrom']) {
                      setErrors(prev => ({ ...prev, activeFrom: '' }));
                    }
                  }}
                  onInvalidChange={(invalid) => {
                    setActiveFromInvalid(invalid);
                    // Clearing the field fires no onChange (null to null), so
                    // the half-typed message is dropped here instead.
                    if (!invalid && errors['activeFrom'] === INCOMPLETE_DATE_MESSAGE) {
                      setErrors(prev => ({ ...prev, activeFrom: '' }));
                    }
                  }}
                  required
                  error={!!errors['activeFrom']}
                  helperText={errors['activeFrom']}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <AppDatePicker
                  label="Aktiv till (valfritt)"
                  value={activeUntilDate}
                  onChange={(newValue) => {
                    setActiveUntilDate(newValue);
                    if (errors['activeUntil']) {
                      setErrors(prev => ({ ...prev, activeUntil: '' }));
                    }
                  }}
                  onInvalidChange={(invalid) => {
                    setActiveUntilInvalid(invalid);
                    // Clearing the field fires no onChange (null to null), so
                    // the half-typed message is dropped here instead.
                    if (!invalid && errors['activeUntil'] === INCOMPLETE_DATE_MESSAGE) {
                      setErrors(prev => ({ ...prev, activeUntil: '' }));
                    }
                  }}
                  error={!!errors['activeUntil']}
                  helperText={errors['activeUntil'] || 'Lämna tomt för obegränsad giltighetstid'}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Beskrivning"
                  value={formData.description}
                  onChange={handleChange('description')}
                  error={!!errors['description']}
                  helperText={errors['description']}
                  required
                  multiline
                  rows={3}
                  placeholder="Beskriv syftet och innehållet för denna kontrolltyp..."
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <ControlPeriodSelect
                  value={period}
                  onChange={(next) => {
                    setPeriod(next);
                    if (errors['periodMonths']) {
                      setErrors(prev => ({ ...prev, periodMonths: '' }));
                    }
                  }}
                  monthsError={errors['periodMonths']}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Formulärkonfiguration kommer att kunna definieras efter att kontrolltypen har skapats.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>
          <Button 
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Skapar...' : 'Skapa kontrolltyp'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateControlTypeModal;